import { useCallback, useContext } from "react"

import { ToastAction, ToastAnchorHorizontal, ToastContext, ToastState } from "./toast-context"

export type MakeToast = (toastState: ToastState) => void
export type MakeSuccessToast = (message: string, action?: ToastAction) => void
export type MakeErrorToast = (message: string, action?: ToastAction) => void
export type MakeFavoriteToast = (added: boolean, anchorHorizontal?: ToastAnchorHorizontal) => void

export const useToast = (): {
	makeToast: MakeToast
	makeSuccessToast: MakeSuccessToast
	makeErrorToast: MakeErrorToast
	makeFavoriteToast: MakeFavoriteToast
} => {
	const toastContext = useContext(ToastContext)

	if (!toastContext) {
		throw new Error("useToast must be used within a ToastProvider")
	}

	const setState = toastContext[1]

	const makeToast = (toastState: ToastState) => {
		setState(toastState)
	}

	const makeSuccessToast: MakeSuccessToast = useCallback(
		(message, action) => {
			setState({
				message,
				iconName: "check-circle",
				iconColor: "success",
				iconFamily: "solid",
				action,
			})
		},
		[setState]
	)

	const makeErrorToast: MakeErrorToast = useCallback(
		(message, action) => {
			setState({
				message,
				iconName: "exclamation-triangle",
				iconColor: "error",
				iconFamily: "solid",
				action,
			})
		},
		[setState]
	)

	const makeFavoriteToast: MakeFavoriteToast = useCallback(
		(added, anchorHorizontal = "right") => {
			setState({
				message:
					added ? "Item added to your favorites." : "Item removed from your favorites.",
				iconName: "heart",
				iconFamily: added ? "solid" : undefined,
				variant: "light",
				anchorHorizontal,
			})
		},
		[setState]
	)

	return {
		makeToast,
		makeSuccessToast,
		makeErrorToast,
		makeFavoriteToast,
	}
}
